<template>
  <div class="stock-view-page">
    <StockDocumentPageHeading
      :title="$t('STOCK.SALES_ORDERS.PAGE_TITLE')"
      :code="isNew ? '' : document.code"
      :new-label="isNew ? $t('STOCK.SALES_ORDERS.NEW') : ''"
    />
    <v-card :loading="isProcessing" :disabled="isProcessing">
      <v-tabs v-model="tab" class="stock-color" left dark>
        <v-tab href="#header">{{ document.code || $t("STOCK.SALES_ORDERS.NEW") }}</v-tab>
        <v-spacer></v-spacer>
        <div class="buttons-tab d-flex align-center flex-wrap pa-2">
          <v-chip v-if="document.status" class="mr-2" :color="statusColor" small dark>
            {{ statusLabel }}
          </v-chip>
          <v-btn
            v-if="canConfirm"
            class="mr-2 success"
            small
            @click="confirmOrder"
          >
            {{ $t("STOCK.SALES_ORDERS.CONFIRM") }}
          </v-btn>
          <v-btn
            v-if="canOrganizeTransport"
            class="mr-2 primary"
            small
            @click="organizeTransport"
          >
            {{ $t("STOCK.TRANSPORT_ORDERS.ORGANIZE_TRANSPORT") }}
          </v-btn>
          <v-btn
            v-if="canCreatePoFromSo"
            class="mr-2 primary"
            small
            @click="openCreatePoDialog"
          >
            {{ $t("STOCK.SALES_ORDERS.CREATE_PO_FROM_SO") }}
          </v-btn>
          <v-btn
            v-if="canCreateProforma"
            class="mr-2 primary"
            small
            @click="createProformaInvoice"
          >
            {{ $t("STOCK.INVOICES.CREATE_PROFORMA") }}
          </v-btn>
          <v-btn
            v-if="canCreateDelivery"
            class="mr-2 primary"
            small
            @click="createDelivery"
          >
            {{ $t("STOCK.SALES_ORDERS.CREATE_DELIVERY") }}
          </v-btn>
          <v-btn
            v-if="canCancel"
            class="mr-2 error"
            small
            @click="cancelOrder"
          >
            {{ $t("GENERAL.LABELS.CANCEL") }}
          </v-btn>
          <v-btn
            v-if="canEditDocument"
            class="mr-2 success"
            small
            @click="save"
          >
            {{ $t("GENERAL.LABELS.SAVE") }}
          </v-btn>
        </div>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item value="header">
          <v-card flat>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="document.organization_id"
                    :items="organizations"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.WAREHOUSES.ORGANIZATION') + '*'"
                    :disabled="headerFieldDisabled('organization_id')"
                    :rules="headerFieldDisabled('organization_id') ? [] : [validations.required()]"
                    :error-messages="validationErrors.organization_id || []"
                    @change="clearHeaderValidationError('organization_id')"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="3">
                  <v-switch
                    v-model="intercompanyOnly"
                    :label="$t('STOCK.SALES_ORDERS.INTERCOMPANY')"
                    :hint="$t('STOCK.SALES_ORDERS.INTERCOMPANY_HINT')"
                    persistent-hint
                    :disabled="!canEditDocument"
                    dense
                    hide-details="auto"
                    class="mt-0"
                    @change="onIntercompanyOnlyChange"
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="document.customer_company_id"
                    :items="filteredCompanyOptions"
                    :search-input.sync="companySearch"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.SALES_ORDERS.CUSTOMER') + '*'"
                    :disabled="headerFieldDisabled('customer_company_id')"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col
                  v-if="customerRepresentsOrganization"
                  cols="12"
                  md="3"
                >
                  <v-autocomplete
                    v-model="document.counterparty_purchase_order_id"
                    :items="counterpartyPurchaseOrderOptions"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.SALES_ORDERS.COUNTERPARTY_PURCHASE_ORDER')"
                    :hint="$t('STOCK.SALES_ORDERS.COUNTERPARTY_PURCHASE_ORDER_HINT')"
                    persistent-hint
                    :disabled="!canEditCounterpartyLink"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="document.source_warehouse_id"
                    :items="warehouseOptions"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.SALES_ORDERS.SOURCE_WAREHOUSE') + '*'"
                    :hint="$t('STOCK.SALES_ORDERS.SOURCE_WAREHOUSE_HINT')"
                    persistent-hint
                    :disabled="headerFieldDisabled('source_warehouse_id')"
                    :rules="
                      headerFieldDisabled('source_warehouse_id')
                        ? []
                        : [validations.required()]
                    "
                    :error-messages="validationErrors.source_warehouse_id || []"
                    @change="clearHeaderValidationError('source_warehouse_id')"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="document.deal_id"
                    :items="dealOptions"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.SALES_ORDERS.DEAL')"
                    :disabled="headerFieldDisabled('deal_id')"
                    clearable
                    @change="onDealChange"
                  ></v-autocomplete>
                  <StockDealReference
                    :deal-id="document.deal_id"
                    :deal="document.deal || selectedDealOption"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="2">
                  <DatePicker
                    v-model="document.order_date"
                    :label="$t('STOCK.SALES_ORDERS.ORDER_DATE') + '*'"
                    :disabled="headerFieldDisabled('order_date')"
                    without-hint
                  ></DatePicker>
                </v-col>
                <v-col cols="12" md="2">
                  <DatePicker
                    v-model="document.requested_delivery_date"
                    :label="$t('STOCK.SALES_ORDERS.REQUESTED_DELIVERY')"
                    :disabled="headerFieldDisabled('requested_delivery_date')"
                    without-hint
                    clearable
                  ></DatePicker>
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field
                    v-model.number="document.default_vat_rate"
                    type="number"
                    :label="$t('STOCK.SALES_ORDERS.DEFAULT_VAT')"
                    suffix="%"
                    :disabled="headerFieldDisabled('default_vat_rate')"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="document.currency_code"
                    :label="$t('STOCK.SALES_ORDERS.CURRENCY')"
                    :disabled="headerFieldDisabled('currency_code')"
                  ></v-text-field>
                </v-col>
                <StockIncotermFields
                  :incoterm-id.sync="document.incoterm_id"
                  :incoterm-named-place.sync="document.incoterm_named_place"
                  :incoterm-options="incotermOptions"
                  :incoterm-disabled="headerFieldDisabled('incoterm_id')"
                  :named-place-disabled="headerFieldDisabled('incoterm_named_place')"
                />
                <v-col cols="12" md="3">
                  <v-textarea
                    v-model="document.notes"
                    rows="1"
                    auto-grow
                    :label="$t('GENERAL.LABELS.NOTES')"
                    :disabled="headerFieldDisabled('notes')"
                  ></v-textarea>
                </v-col>
              </v-row>

              <v-alert
                v-if="isAmendMode"
                type="info"
                dense
                outlined
                class="mb-4"
              >
                {{ $t("STOCK.SALES_ORDERS.AMEND_HINT") }}
              </v-alert>

              <v-alert type="info" dense text class="mb-4">
                {{ $t("STOCK.SALES_ORDERS.TOTALS_SUMMARY", {
                  net: formatNum(previewDocumentTotals.total_net),
                  vat: formatNum(previewDocumentTotals.total_vat),
                  gross: formatNum(previewDocumentTotals.total_gross),
                }) }}
              </v-alert>

              <v-subheader class="px-0">{{ $t("STOCK.SALES_ORDERS.LINES") }}</v-subheader>

              <v-alert v-if="!lines.length" type="info" dense text class="mb-3">
                {{ $t("STOCK.PRODUCT_FINDER.EMPTY_LINES") }}
              </v-alert>
              <v-simple-table v-else dense class="stock-document-lines">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{{ $t("STOCK.PRODUCTS.PRODUCT") }}</th>
                    <th>{{ $t("STOCK.SALES_ORDERS.QTY") }}</th>
                    <th>{{ $t("STOCK.SALES_ORDERS.UNIT_PRICE_NET") }}</th>
                    <th>{{ $t("STOCK.SALES_ORDERS.LINE_TOTAL_NET") }}</th>
                    <th>{{ $t("STOCK.SALES_ORDERS.VAT") }}</th>
                    <th>{{ $t("STOCK.SALES_ORDERS.FULFILLED") }}</th>
                    <th>{{ $t("STOCK.SALES_ORDERS.REMAINING") }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(line, index) in lines"
                    :key="`line-${line.id || index}-${line.stock_product_id}`"
                  >
                    <td>{{ index + 1 }}</td>
                    <td class="stock-document-lines__product">
                      <div class="font-weight-medium">{{ lineProductLabel(line) }}</div>
                      <div class="text-caption grey--text">{{ lineProductMeta(line) }}</div>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="line.ordered_quantity"
                        type="number"
                        solo
                        dense
                        hide-details
                        class="stock-document-lines__field"
                        :suffix="lineUnitCode(line)"
                        :disabled="!canEditDocument"
                        :hint="lineQtyHint(line)"
                        persistent-hint
                        @focus="focusSelectAllInput"
                        @input="onLineQtyOrUnitInput(line, lineQuantityKey)"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="line.unit_price_net"
                        type="number"
                        solo
                        dense
                        hide-details
                        class="stock-document-lines__field"
                        :suffix="document.currency_code || ''"
                        :disabled="!canEditDocument"
                        @focus="focusSelectAllInput"
                        @input="onLineQtyOrUnitInput(line, lineQuantityKey)"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="line.line_total_net"
                        type="number"
                        solo
                        dense
                        hide-details
                        class="stock-document-lines__field"
                        :suffix="document.currency_code || ''"
                        :disabled="!canEditDocument"
                        @focus="focusSelectAllInput"
                        @input="onLineTotalNetInput(line, lineQuantityKey)"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="line.vat_rate"
                        type="number"
                        solo
                        dense
                        hide-details
                        class="stock-document-lines__field stock-document-lines__field--narrow"
                        :disabled="!canEditDocument"
                        @focus="focusSelectAllInput"
                        @input="onLineVatRateInput"
                      ></v-text-field>
                    </td>
                    <td class="stock-document-lines__metric">
                      {{ fulfillmentFor(line).fulfilled }}
                    </td>
                    <td class="stock-document-lines__metric">
                      {{ fulfillmentFor(line).remaining }}
                    </td>
                    <td>
                      <v-btn
                        v-if="canEditDocument && canRemoveLine(line)"
                        icon
                        small
                        @click="removeLine(index)"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                      <v-tooltip v-else-if="canEditDocument && lineHasDeliveries(line)" bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <span v-bind="attrs" v-on="on">
                            <v-btn icon small disabled>
                              <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                          </span>
                        </template>
                        <span>{{ $t("STOCK.SALES_ORDERS.LINE_CANNOT_REMOVE") }}</span>
                      </v-tooltip>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>

              <StockProductLineFinder
                :disabled="!canEditDocument"
                @add="addProductFromFinder"
              />
              <v-subheader v-if="statusTimeline.length" class="px-0 mt-4">
                {{ $t("STOCK.SALES_ORDERS.STATUS_TIMELINE") }}
              </v-subheader>
              <v-timeline v-if="statusTimeline.length" dense class="so-status-timeline">
                <v-timeline-item
                  v-for="entry in statusTimeline"
                  :key="entry.id"
                  small
                  :color="timelineEntryColor(entry)"
                >
                  <div class="subtitle-2">
                    {{ timelineEntryLabel(entry) }}
                  </div>
                  <div class="caption grey--text">
                    {{ formatTimelineDate(entry.changed_at) }}
                    <span v-if="entry.changed_by_user && entry.changed_by_user.name">
                      · {{ entry.changed_by_user.name }}
                    </span>
                  </div>
                  <div v-if="entry.notes" class="caption">{{ entry.notes }}</div>
                </v-timeline-item>
              </v-timeline>

              <StockSalesOrderMovementsTable
                v-if="!isNew"
                ref="soMovements"
                :sales-order-id="salesOrderId"
              />

              <StockLinkedTransportOrdersTable
                v-if="!isNew"
                :items="transportOrders"
                :title="$t('STOCK.SALES_ORDERS.RELATED_TRANSPORT_ORDERS')"
                :empty-message="$t('STOCK.SALES_ORDERS.NO_RELATED_TRANSPORT_ORDERS')"
              />

              <StockDocumentFilesPanel
                v-if="!isNew"
                document-kind="sales_order"
                :document-id="salesOrderId"
                :files="documentFiles"
                :can-regenerate="canRegenerateDocument"
                @files-updated="documentFiles = $event"
              />
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <v-dialog v-model="createPoDialog" max-width="480">
      <v-card>
        <v-card-title>{{ $t("STOCK.SALES_ORDERS.CREATE_PO_FROM_SO") }}</v-card-title>
        <v-card-text>
          <v-switch
            v-model="createPoIntercompanyOnly"
            :label="$t('STOCK.PURCHASE_ORDERS.INTERCOMPANY')"
            :hint="$t('STOCK.PURCHASE_ORDERS.INTERCOMPANY_HINT')"
            persistent-hint
            dense
            hide-details="auto"
            class="mb-4"
            @change="onCreatePoIntercompanyOnlyChange"
          />
          <v-autocomplete
            v-model="createPoSupplierId"
            :items="filteredCreatePoSupplierOptions"
            :search-input.sync="supplierSearch"
            item-text="label"
            item-value="id"
            :label="$t('STOCK.PURCHASE_ORDERS.SUPPLIER') + '*'"
            clearable
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="createPoDialog = false">
            {{ $t("GENERAL.LABELS.CANCEL") }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="createPoLoading"
            :disabled="!createPoSupplierId"
            @click="confirmCreatePoFromSo"
          >
            {{ $t("GENERAL.LABELS.CREATE_BUTTON") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { debounce } from "lodash";
import StockProductLineFinder from "@/views/partials/stock/StockProductLineFinder.vue";
import StockSalesOrderMovementsTable from "@/views/partials/stock/StockSalesOrderMovementsTable.vue";
import StockLinkedTransportOrdersTable from "@/views/partials/stock/StockLinkedTransportOrdersTable.vue";
import StockDocumentPageHeading from "@/views/partials/stock/StockDocumentPageHeading.vue";
import StockDocumentFilesPanel from "@/views/partials/stock/StockDocumentFilesPanel.vue";
import StockDealReference from "@/views/partials/stock/StockDealReference.vue";
import StockIncotermFields from "@/views/partials/stock/StockIncotermFields.vue";
import DatePicker from "@/views/partials/form/DatePicker.vue";
import stockDocumentLinesMixin from "@/views/partials/stock/stockDocumentLines.mixin";
import stockDocumentOptionsMixin from "@/views/partials/stock/stockDocumentOptions.mixin";
import stockDocumentTotalsMixin from "@/views/partials/stock/stockDocumentTotals.mixin";
import stockDocumentContextMixin from "@/views/partials/stock/stockDocumentContext.mixin";
import stockIncotermMixin from "@/views/partials/stock/stockIncoterm.mixin";
import { GET_COMPANIES_OPTIONS } from "@/store/companies.module";
import {
  GET_STOCK_SALES_ORDER,
  CREATE_STOCK_SALES_ORDER,
  UPDATE_STOCK_SALES_ORDER,
  CONFIRM_STOCK_SALES_ORDER,
  CANCEL_STOCK_SALES_ORDER,
  GET_STOCK_SO_COUNTERPARTY_PO_OPTIONS,
} from "@/store/stock/stockSalesOrders.module";
import { GET_STOCK_DEAL_OPTIONS } from "@/store/stock/stockReceipts.module";
import { CREATE_STOCK_PROFORMA_FROM_SALES_ORDER } from "@/store/stock/stockInvoices.module";
import { SHOW_SNACK } from "@/store/snack.module";

const emptyLine = (vat) => ({
  stock_product_id: null,
  ordered_quantity: 1,
  unit_id: null,
  unit_code: null,
  unit_price_net: 0,
  line_total_net: 0,
  vat_rate: vat,
});

export default {
  name: "StockViewSalesOrder",
  components: {
    StockProductLineFinder,
    StockSalesOrderMovementsTable,
    StockLinkedTransportOrdersTable,
    StockDocumentPageHeading,
    StockDocumentFilesPanel,
    StockDealReference,
    StockIncotermFields,
    DatePicker,
  },
  mixins: [
    stockDocumentLinesMixin,
    stockDocumentOptionsMixin,
    stockDocumentTotalsMixin,
    stockDocumentContextMixin,
    stockIncotermMixin,
  ],
  data() {
    return {
      lineQuantityKey: "ordered_quantity",
      tab: "header",
      isProcessing: false,
      document: this.emptyDocument(),
      lines: [],
      fulfillment: {},
      transportOrders: [],
      organizations: [],
      companyOptions: [],
      intercompanyOnly: false,
      counterpartyPurchaseOrderOptions: [],
      warehouseOptions: [],
      dealOptions: [],
      companySearch: null,
      validationErrors: {},
      documentFiles: [],
      createPoDialog: false,
      createPoSupplierId: null,
      createPoIntercompanyOnly: false,
      createPoLoading: false,
      supplierOptions: [],
      supplierSearch: null,
    };
  },
  computed: {
    isNew() {
      return !this.$route.params.id;
    },
    isAmendMode() {
      return ["confirmed", "partially_delivered"].includes(this.document.status);
    },
    selectedCustomerOrganizationId() {
      const customer = (this.companyOptions || []).find(
        (company) => company.id === this.document.customer_company_id
      );
      return customer?.organization_id || null;
    },
    customerRepresentsOrganization() {
      return Boolean(this.selectedCustomerOrganizationId);
    },
    filteredCompanyOptions() {
      const options = this.companyOptions || [];
      if (!this.intercompanyOnly) {
        return options;
      }
      return options.filter((company) => company.organization_id);
    },
    filteredCreatePoSupplierOptions() {
      const options = this.supplierOptions || [];
      if (!this.createPoIntercompanyOnly) {
        return options;
      }
      return options.filter((company) => company.organization_id);
    },
    canEditCounterpartyLink() {
      return this.canEditDocument;
    },
    canEditDocument() {
      if (this.isNew) {
        return this.hasPermissions("stock-sales-orders-create");
      }
      const editableStatus = ["draft", "confirmed", "partially_delivered"].includes(
        this.document.status
      );
      return editableStatus && this.hasPermissions("stock-sales-orders-edit");
    },
    canConfirm() {
      return (
        !this.isNew &&
        this.document.status === "draft" &&
        this.hasPermissions("stock-sales-orders-confirm")
      );
    },
    canCreateDelivery() {
      return (
        !this.isNew &&
        ["confirmed", "partially_delivered"].includes(this.document.status) &&
        this.hasPermissions("stock-sales-create")
      );
    },
    canCreateProforma() {
      return (
        !this.isNew &&
        !["cancelled", "closed"].includes(this.document.status) &&
        this.hasPermissions("stock-invoices-create")
      );
    },
    canOrganizeTransport() {
      return (
        !this.isNew &&
        ["confirmed", "partially_delivered"].includes(this.document.status) &&
        this.hasPermissions("stock-transport-orders-create")
      );
    },
    canCreatePoFromSo() {
      return (
        !this.isNew &&
        ["confirmed", "partially_delivered"].includes(this.document.status) &&
        this.lines.some((line) => line.stock_product_id) &&
        this.hasPermissions("stock-purchase-orders-create")
      );
    },
    canRegenerateDocument() {
      return ["confirmed", "partially_delivered", "delivered", "invoiced", "closed"].includes(
        this.document.status
      );
    },
    canCancel() {
      return (
        !this.isNew &&
        !["cancelled", "delivered", "closed"].includes(this.document.status) &&
        this.hasPermissions("stock-sales-orders-cancel")
      );
    },
    statusColor() {
      const map = {
        draft: "grey",
        confirmed: "primary",
        partially_delivered: "orange",
        delivered: "success",
        invoiced: "blue",
        closed: "grey darken-2",
        cancelled: "error",
      };
      return map[this.document.status] || "grey";
    },
    statusLabel() {
      const key = `STOCK.STATUSES.SO.${(this.document.status || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : this.document.status;
    },
    salesOrderId() {
      const id = this.$route.params.id;
      return id ? Number(id) : null;
    },
    statusTimeline() {
      const timeline = this.document.status_timeline;
      if (!Array.isArray(timeline)) {
        return [];
      }
      return [...timeline].sort(
        (a, b) => new Date(b.changed_at) - new Date(a.changed_at)
      );
    },
  },
  watch: {
    companySearch: debounce(function () {
      this.loadCompanies();
    }, 300),
    supplierSearch: debounce(function onSupplierSearch() {
      if (this.createPoDialog) {
        this.loadSuppliers();
      }
    }, 300),
    "document.organization_id"() {
      this.runStockHeaderReloadOnOrganizationChange();
      this.loadCounterpartyPurchaseOrderOptions();
    },
    "document.customer_company_id"() {
      this.loadCounterpartyPurchaseOrderOptions();
    },
    "document.default_vat_rate"(value) {
      this.lines.forEach((line) => {
        if (line.vat_rate === null || line.vat_rate === undefined) {
          line.vat_rate = value;
        }
      });
    },
  },
  mounted() {
    this.bootstrap();
  },
  methods: {
    openCreatePoDialog() {
      const dealSuppliers = this.normalizeDealCompanies(
        this.resolveDealForParties()?.suppliers
      );
      if (dealSuppliers.length === 1) {
        this.createPoSupplierId = dealSuppliers[0].id;
        this.confirmCreatePoFromSo();
        return;
      }
      this.createPoSupplierId = null;
      this.createPoIntercompanyOnly = false;
      this.createPoDialog = true;
      this.loadSuppliers();
    },
    async loadSuppliers() {
      const dealSuppliers = this.normalizeDealCompanies(
        this.resolveDealForParties()?.suppliers
      );
      if (!this.supplierSearch && dealSuppliers.length) {
        this.supplierOptions = dealSuppliers;
        return;
      }
      try {
        const data = await this.$store.dispatch(GET_COMPANIES_OPTIONS, {
          search: this.supplierSearch || "",
          itemsPerPage: 25,
        });
        const fetched = Array.isArray(data) ? data : data.data || [];
        this.supplierOptions = dealSuppliers.length
          ? this.mergeDealSuppliersIntoCreatePoOptions(fetched, dealSuppliers)
          : fetched;
      } catch (e) {
        this.supplierOptions = dealSuppliers;
      }
    },
    mergeDealSuppliersIntoCreatePoOptions(options, dealSuppliers) {
      let merged = options || [];
      (dealSuppliers || []).forEach((company) => {
        if (!merged.some((item) => Number(item.id) === Number(company.id))) {
          merged = [{ ...company, id: Number(company.id) }, ...merged];
        }
      });
      return merged;
    },
    onCreatePoIntercompanyOnlyChange(enabled) {
      if (!enabled || !this.createPoSupplierId) {
        return;
      }
      const supplier = (this.supplierOptions || []).find(
        (company) => Number(company.id) === Number(this.createPoSupplierId)
      );
      if (supplier && !supplier.organization_id) {
        this.createPoSupplierId = null;
      }
    },
    confirmCreatePoFromSo() {
      if (!this.createPoSupplierId) {
        return;
      }
      this.createPoDialog = false;
      this.$router.push({
        name: "stock-purchase-orders-create",
        query: {
          sales_order_id: this.$route.params.id,
          supplier_company_id: this.createPoSupplierId,
          deal_id: this.document.deal_id || undefined,
          organization_id: this.document.organization_id || undefined,
        },
      });
    },
    emptyDocument() {
      return {
        organization_id: null,
        customer_company_id: null,
        counterparty_purchase_order_id: null,
        source_warehouse_id: null,
        deal_id: null,
        order_date: new Date().toISOString().slice(0, 10),
        requested_delivery_date: null,
        default_vat_rate: 20,
        currency_code: "EUR",
        exchange_rate_to_base: 1,
        incoterm_id: null,
        incoterm_named_place: null,
        notes: null,
        status: "draft",
        code: null,
        total_net: 0,
        total_vat: 0,
        total_gross: 0,
      };
    },
    computeLineNetFromUnit(line, quantityKey) {
      const qty = Number(line[quantityKey] ?? line.quantity ?? 0);
      const unitPrice = Number(line.unit_price_net || 0);
      return this.roundMoney(qty * unitPrice);
    },
    mapLineFromApi(item, data, quantityKey) {
      const qty = Number(item[quantityKey] ?? item.quantity ?? 0);
      const unitPrice = Number(item.unit_price_net || 0);
      const lineTotal =
        item.line_total_net != null
          ? Number(item.line_total_net)
          : this.roundMoney(qty * unitPrice);

      return {
        id: item.id,
        stock_product_id: item.stock_product_id,
        stock_product: item.stock_product || null,
        [quantityKey]: qty,
        unit_price_net: unitPrice,
        line_total_net: lineTotal,
        vat_rate: Number(item.vat_rate ?? data.default_vat_rate ?? 20),
        ...this.mapLineUnitFields(item),
      };
    },
    onLineTotalNetInput(line, quantityKey) {
      const qty = Number(line[quantityKey] ?? line.quantity ?? 0);
      const total = Number(line.line_total_net || 0);
      line.line_total_net = total;
      if (qty > 0) {
        line.unit_price_net = this.roundMoney(total / qty);
      }
    },
    validateOrganizationAndWarehouseRequired() {
      let valid = true;
      const errors = { ...(this.validationErrors || {}) };

      if (!this.document.organization_id) {
        errors.organization_id = [this.$t("GENERAL.VALIDATION.REQUIRED_FIELD")];
        valid = false;
      } else {
        delete errors.organization_id;
      }

      if (!this.document.source_warehouse_id) {
        errors.source_warehouse_id = [this.$t("GENERAL.VALIDATION.REQUIRED_FIELD")];
        valid = false;
      } else {
        delete errors.source_warehouse_id;
      }

      if (this.validationErrors !== undefined) {
        this.validationErrors = errors;
      }

      if (!valid) {
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.DOCUMENTS.ORG_AND_WAREHOUSE_REQUIRED_SAVE"),
          color: "error",
        });
      }

      return valid;
    },
    resetDocumentSelectionsOutsideContext() {
      if (this.suppressStockHeaderReload) {
        return;
      }

      const orgId = this.document.organization_id;
      if (
        orgId &&
        !this.organizations.some((org) => Number(org.id) === Number(orgId))
      ) {
        if (!(this.isNew && this.headerOptionsSeed)) {
          this.document.organization_id = null;
          this.document.source_warehouse_id = null;
        }
        return;
      }

      const warehouseId = this.document.source_warehouse_id;
      if (
        warehouseId &&
        !this.warehouseOptions.some(
          (warehouse) => Number(warehouse.id) === Number(warehouseId)
        )
      ) {
        if (typeof this.reconcileHeaderOptionsFromSeed === "function") {
          this.reconcileHeaderOptionsFromSeed();
        }
        if (
          !this.warehouseOptions.some(
            (warehouse) => Number(warehouse.id) === Number(warehouseId)
          ) &&
          !(this.isNew && this.headerOptionsSeed)
        ) {
          this.document.source_warehouse_id = null;
        }
      }
    },
    applyStockContextDefaultsOnNewDocument() {
      if (!this.isNew) {
        return;
      }

      if (!this.document.organization_id && this.organizations.length === 1) {
        this.document.organization_id = this.organizations[0].id;
      }

      if (!this.document.source_warehouse_id && this.warehouseOptions.length === 1) {
        this.document.source_warehouse_id = this.warehouseOptions[0].id;
      }

      this.applyRouteQueryDefaultsOnNewDocument();
    },
    async bootstrap() {
      this.isProcessing = true;
      try {
        await this.loadOrganizations();
        await this.loadCompanies();
        await this.loadWarehouses();
        await this.loadDeals();
        await this.loadIncoterms();

        if (!this.isNew) {
          const data = await this.$store.dispatch(
            GET_STOCK_SALES_ORDER,
            this.$route.params.id
          );
          await this.applyDocument(data);
        } else {
          this.applyStockContextDefaultsOnNewDocument();
        }
      } finally {
        this.isProcessing = false;
      }
    },
    async applyDocument(data) {
      if (data.deal) {
        this.setSelectedDealCache(data.deal);
      } else if (!data.deal_id) {
        this.selectedDealOption = null;
      }
      this.document = {
        ...this.emptyDocument(),
        ...data,
        ...this.mapSalesOrderDocument(data),
      };
      await this.reloadSalesOrderHeaderOptions();
      this.syncSalesOrderHeaderOptions(data);
      await this.loadCounterpartyPurchaseOrderOptions();
      this.fulfillment = data.fulfillment || {};
      this.transportOrders = Array.isArray(data.transport_orders) ? data.transport_orders : [];
      this.lines = (data.items || []).map((item) => {
        const line = this.mapLineFromApi(item, data, this.lineQuantityKey);
        line.id = item.id;
        line.fulfillment =
          item.fulfillment ??
          this.fulfillment[item.id] ??
          this.fulfillment[String(item.id)] ??
          null;
        return line;
      });
      this.documentFiles = Array.isArray(data.files) ? data.files : [];
      this.syncIncotermFromDocument(data);
      this.syncIntercompanyFlagFromCustomer(data);
      this.$nextTick(() => this.$refs.soMovements?.reload());
    },
    payload() {
      return {
        ...this.document,
        items: this.lines
          .filter((line) => line.stock_product_id)
          .map((line, index) => ({
            id: line.id || undefined,
            line_no: index + 1,
            stock_product_id: line.stock_product_id,
            ordered_quantity: line.ordered_quantity,
            unit_id: line.unit_id,
            unit_price_net: line.unit_price_net,
            vat_rate: line.vat_rate ?? this.document.default_vat_rate,
          })),
      };
    },
    async save() {
      if (!this.validateOrganizationAndWarehouseRequired()) {
        return;
      }
      this.isProcessing = true;
      try {
        this.validationErrors = {};
        const params = this.payload();
        let data;
        if (this.isNew) {
          data = await this.$store.dispatch(CREATE_STOCK_SALES_ORDER, params);
          this.$router.replace({
            name: "stock-sales-orders-view",
            params: { id: data.id },
          });
        } else {
          data = await this.$store.dispatch(UPDATE_STOCK_SALES_ORDER, {
            id: this.$route.params.id,
            params,
          });
        }
        await this.applyDocument(data);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          color: "success",
        });
      } catch (errors) {
        this.validationErrors = (errors && errors.errors) || {};
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async confirmOrder() {
      if (!this.validateOrganizationAndWarehouseRequired()) {
        return;
      }
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(
          CONFIRM_STOCK_SALES_ORDER,
          this.$route.params.id
        );
        await this.applyDocument(data);
      } finally {
        this.isProcessing = false;
      }
    },
    async cancelOrder() {
      if (!confirm(this.$t("STOCK.SALES_ORDERS.CONFIRM_CANCEL"))) return;
      const data = await this.$store.dispatch(
        CANCEL_STOCK_SALES_ORDER,
        this.$route.params.id
      );
      await this.applyDocument(data);
    },
    organizeTransport() {
      this.$router.push({
        name: "stock-transport-orders-create",
        query: {
          sales_order_id: this.$route.params.id,
          direction: "outbound",
          deal_id: this.document.deal_id || undefined,
          organization_id: this.document.organization_id || undefined,
        },
      });
    },
    createDelivery() {
      if (!this.document.source_warehouse_id) {
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.SALES_ORDERS.WAREHOUSE_REQUIRED_DELIVERY"),
          color: "error",
        });
        return;
      }
      this.$router.push({
        name: "stock-sales-create",
        query: {
          sales_order_id: this.$route.params.id,
          deal_id: this.document.deal_id || undefined,
          organization_id: this.document.organization_id || undefined,
        },
      });
    },
    async createProformaInvoice() {
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(CREATE_STOCK_PROFORMA_FROM_SALES_ORDER, {
          salesOrderId: this.$route.params.id,
        });
        this.$router.push({ name: "stock-invoices-view", params: { id: data.id } });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    addProductFromFinder(product) {
      this.addProductLine(product, emptyLine);
    },
    removeLine(index) {
      this.lines.splice(index, 1);
    },
    headerFieldDisabled(field) {
      if (!this.canEditDocument) {
        return true;
      }
      if (!this.isAmendMode) {
        return false;
      }
      if ([
        "deal_id",
        "requested_delivery_date",
        "notes",
        "incoterm_id",
        "incoterm_named_place",
        "source_warehouse_id",
      ].includes(field)) {
        return false;
      }
      return true;
    },
    lineFulfilledQty(line) {
      const row = line.fulfillment || {};
      return Number(row.fulfilled_quantity || 0);
    },
    lineHasDeliveries(line) {
      return this.lineFulfilledQty(line) > 0.0001;
    },
    canRemoveLine(line) {
      return !this.lineHasDeliveries(line);
    },
    lineQtyHint(line) {
      if (!this.isAmendMode || !this.lineHasDeliveries(line)) {
        return "";
      }
      return `min ${this.formatNum(this.lineFulfilledQty(line))}`;
    },
    fulfillmentFor(line) {
      const row =
        line.fulfillment ||
        this.fulfillment[line.id] ||
        this.fulfillment[String(line.id)] ||
        {};
      const formatQty = (value) =>
        value != null && value !== "" ? this.formatNum(value) : "-";
      return {
        fulfilled: formatQty(row.fulfilled_quantity),
        remaining: formatQty(row.remaining_quantity),
      };
    },
    timelineEntryLabel(entry) {
      const slug = entry.status && entry.status.slug ? entry.status.slug : "";
      const key = `STOCK.STATUSES.SO.${slug.toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : (entry.status && entry.status.name) || slug || "-";
    },
    timelineEntryColor(entry) {
      const slug = entry.status && entry.status.slug;
      const map = {
        draft: "grey",
        confirmed: "primary",
        partially_delivered: "orange",
        delivered: "success",
        cancelled: "error",
      };
      return map[slug] || "grey";
    },
    formatTimelineDate(value) {
      if (!value) {
        return "-";
      }
      return new Date(value).toLocaleString();
    },
    async loadCompanies() {
      const data = await this.$store.dispatch(GET_COMPANIES_OPTIONS, {
        search: this.companySearch,
      });
      this.companyOptions = data.data || data;
      this.preserveDealPartyCompaniesInOptions();
    },
    syncIntercompanyFlagFromCustomer(data) {
      const customer =
        data?.customer_company
        || (this.companyOptions || []).find(
          (company) => Number(company.id) === Number(this.document.customer_company_id)
        );
      this.intercompanyOnly = Boolean(customer?.organization_id);
    },
    onIntercompanyOnlyChange(enabled) {
      if (!enabled || !this.document.customer_company_id) {
        return;
      }
      const customer = (this.companyOptions || []).find(
        (company) => Number(company.id) === Number(this.document.customer_company_id)
      );
      if (customer && !customer.organization_id) {
        this.document.customer_company_id = null;
        this.document.counterparty_purchase_order_id = null;
      }
    },
    async loadCounterpartyPurchaseOrderOptions() {
      if (!this.customerRepresentsOrganization || !this.document.organization_id) {
        this.counterpartyPurchaseOrderOptions = [];
        return;
      }

      try {
        this.counterpartyPurchaseOrderOptions = await this.$store.dispatch(
          GET_STOCK_SO_COUNTERPARTY_PO_OPTIONS,
          {
            organization_id: this.document.organization_id,
            customer_company_id: this.document.customer_company_id,
          }
        );
      } catch (error) {
        this.counterpartyPurchaseOrderOptions = [];
      }
    },
    async loadDeals() {
      const responseData = await this.$store.dispatch(GET_STOCK_DEAL_OPTIONS, {
        organization_id: this.document.organization_id,
        limit: 50,
      });
      this.applyDealOptionsResponse(responseData);
    },
    formatNum(value) {
      return Number(value || 0).toFixed(2);
    },
  },
};
</script>

<style scoped>
.stock-view-page {
  padding-bottom: 48px;
  margin-bottom: 24px;
}
.stock-document-lines th,
.stock-document-lines td {
  vertical-align: middle !important;
}
.stock-document-lines__product {
  min-width: 240px;
  max-width: 360px;
}
.stock-document-lines__field {
  max-width: 130px;
}
.stock-document-lines__field--narrow {
  max-width: 90px;
}
.stock-document-lines__metric {
  min-width: 88px;
  text-align: right;
  white-space: nowrap;
}
</style>
